// Desktop / meta UI texture declarations as plain data (no pixi import), so both
// the game loader (render/assets.ts loadUiTextures) and the studio catalog can
// read the same list. Keys match the UiTextures field names.

import type { GearEffectId } from '../battle/types';
import { GEAR_EFFECT_IDS } from '../craft/catalog';
import { RANKS, type RankId } from '../data/ranks';

const UI_ROOT = '/assets/ui';
const SPARKY_ROOT = '/assets/textures/characters/sparky_bot';
const HP_BAR_ROOT = '/assets/textures/hp-bar';

export type UiAssetKey =
  | 'mainBg'
  | 'gameFrame'
  | 'statsWindow'
  | 'ranksWindow'
  | 'paper'
  | 'blueprint'
  | 'tabOn'
  | 'tabOff'
  | 'statCell'
  | 'invCell'
  | 'medalCell'
  | 'statHealth'
  | 'statStrength'
  | 'statArmor'
  | 'statLuck'
  | 'statButtonPlus'
  | 'enemyCardBody'
  | 'enemyAvatarCircle'
  | 'labelBg'
  | 'selectBtn'
  | 'sellBag'
  | 'battleResultWindow'
  | 'punchIcon'
  | 'kickIcon'
  | 'border'
  | 'corner'
  | 'avaBg'
  | 'avaBorder'
  | 'avaPortrait'
  | 'hpBarBorder'
  | 'hpBarBackground'
  | 'hpBarAvatarCircle'
  | 'hpBarMedal';

export type UiAssetDescriptor = { key: UiAssetKey; url: string; label: string };

export const UI_ASSETS: readonly UiAssetDescriptor[] = [
  { key: 'mainBg', url: `${UI_ROOT}/main-bg.png`, label: 'Wrapper' },
  { key: 'gameFrame', url: `${UI_ROOT}/game-window-frame.png`, label: 'Game window frame' },
  { key: 'statsWindow', url: `${UI_ROOT}/stats-window.png`, label: 'Stats window' },
  { key: 'ranksWindow', url: `${UI_ROOT}/ranks-window.png`, label: 'Ranks window' },
  { key: 'paper', url: `${UI_ROOT}/paper.png`, label: 'Paper sheet' },
  { key: 'blueprint', url: `${UI_ROOT}/blueprint.png`, label: 'Blueprint sheet' },
  { key: 'tabOn', url: `${UI_ROOT}/tab-on.png`, label: 'Tab (active)' },
  { key: 'tabOff', url: `${UI_ROOT}/tab-off.png`, label: 'Tab (inactive)' },
  { key: 'statCell', url: `${UI_ROOT}/stat-cell-v2.png`, label: 'Stat row plate' },
  { key: 'invCell', url: `${UI_ROOT}/cells-inv.png`, label: 'Inventory cell' },
  { key: 'medalCell', url: `${UI_ROOT}/medal-cell.png`, label: 'Medal strip plate' },
  { key: 'statHealth', url: `${UI_ROOT}/stats/health.png`, label: 'Stat icon: health' },
  { key: 'statStrength', url: `${UI_ROOT}/stats/strength.png`, label: 'Stat icon: strength' },
  { key: 'statArmor', url: `${UI_ROOT}/stats/armor.png`, label: 'Stat icon: armor' },
  { key: 'statLuck', url: `${UI_ROOT}/stats/luck.png`, label: 'Stat icon: luck' },
  { key: 'statButtonPlus', url: `${UI_ROOT}/stats/button-plus.png`, label: 'Stat upgrade button' },
  { key: 'enemyCardBody', url: `${UI_ROOT}/enemy-card/enemy-card-body.png`, label: 'Enemy card' },
  { key: 'enemyAvatarCircle', url: `${UI_ROOT}/enemy-card/enemy-avatar-circle.png`, label: 'Enemy avatar circle' },
  { key: 'labelBg', url: `${UI_ROOT}/enemy-card/label-bg.png`, label: 'Skill label plate' },
  { key: 'selectBtn', url: `${UI_ROOT}/enemy-card/select-btn-bg.png`, label: 'Button background' },
  { key: 'sellBag', url: `${UI_ROOT}/sell-bag.png`, label: 'Sell zone plate' },
  { key: 'battleResultWindow', url: `${UI_ROOT}/battle-result-window.png`, label: 'Battle result window' },
  { key: 'punchIcon', url: `${SPARKY_ROOT}/punch/ui-log-icon.png`, label: 'Punch icon' },
  { key: 'kickIcon', url: `${SPARKY_ROOT}/kick/ui-log-icon.png`, label: 'Kick icon' },
  { key: 'border', url: `${UI_ROOT}/border.png`, label: 'Modal border' },
  { key: 'corner', url: `${UI_ROOT}/corner.png`, label: 'Modal corner' },
  { key: 'avaBg', url: `${UI_ROOT}/main-ava-background.png`, label: 'Portrait background' },
  { key: 'avaBorder', url: `${UI_ROOT}/sparky-main-ava-border.png`, label: 'Portrait border (gear)' },
  { key: 'avaPortrait', url: `${UI_ROOT}/sparky-main-portrait.png`, label: 'Portrait' },
  { key: 'hpBarBorder', url: `${HP_BAR_ROOT}/hp-bar-border.png`, label: 'HP bar frame' },
  { key: 'hpBarBackground', url: `${HP_BAR_ROOT}/hp-bar-background.png`, label: 'HP bar track' },
  { key: 'hpBarAvatarCircle', url: `${HP_BAR_ROOT}/hp-bar-avatar-circle.png`, label: 'HP bar avatar circle' },
  { key: 'hpBarMedal', url: `${HP_BAR_ROOT}/hp-bar-medal.png`, label: 'HP bar medal' },
] as const;

// Gear effect icons (craft shop + board). Two variants per effect: the square
// shop/tray icon and the round in-gear one; file base name = effect id.
const EFFECTS_UI_ROOT = `${UI_ROOT}/Effects`;

export type EffectIconDescriptor = { key: GearEffectId; url: string; label: string };

export const EFFECT_ICON_ASSETS: readonly EffectIconDescriptor[] = GEAR_EFFECT_IDS.map((id) => ({
  key: id,
  url: `${EFFECTS_UI_ROOT}/${id}.png`,
  label: `Effect: ${id}`,
}));

export const EFFECT_ROUND_ICON_ASSETS: readonly EffectIconDescriptor[] = GEAR_EFFECT_IDS.map(
  (id) => ({
    key: id,
    url: `${EFFECTS_UI_ROOT}/${id}-round.png`,
    label: `Effect: ${id} (round)`,
  }),
);

// Rank badges for the player-info section (left data panel). One icon per tier,
// keyed by rank id. Loaded into UiTextures.ranks (a map, not a flat field).
const RANKS_ROOT = `${UI_ROOT}/ranks`;

export type RankAssetDescriptor = { key: RankId; url: string; label: string };

export const RANK_ASSETS: readonly RankAssetDescriptor[] = RANKS.map((id) => ({
  key: id,
  url: `${RANKS_ROOT}/${id}.png`,
  label: `Rank: ${id}`,
}));

// Buff icons for the buffs row (left data panel). Each badge stands for a
// permanently added stat and carries its own tooltip (shown on hover). Loaded
// into UiTextures.buffIcons (an ordered array) and rendered in StatsPanel.
const ACHIEVEMENTS_ROOT = `${UI_ROOT}/achievements`;

// Tooltip content for a buff badge: a title, the stat bonuses it grants, and a
// flavor note (English, shown to every eligible player). Plain data - edit per
// icon. Each stat line names a stat (so the tooltip picks the matching icon)
// and the bonus value; "shield" reuses the armor icon.
export type BuffStatKey = 'health' | 'strength' | 'luck' | 'shield';
export type BuffStatLine = { stat: BuffStatKey; value: number };
export type BuffTooltip = {
  title: string;
  stats: BuffStatLine[];
  note: string;
};

export type BuffIconDescriptor = { url: string; label: string; tooltip: BuffTooltip };

export const BUFF_ICON_ASSETS: readonly BuffIconDescriptor[] = [
  {
    url: `${ACHIEVEMENTS_ROOT}/beta.png`,
    label: 'Buff: beta',
    tooltip: {
      title: 'Closed Beta Veteran',
      stats: [{ stat: 'luck', value: 1 }, { stat: 'strength', value: 1 }],
      note: 'Granted to every player who fought in the closed beta.',
    },
  },
  {
    url: `${ACHIEVEMENTS_ROOT}/luck.png`,
    label: 'Buff: luck',
    tooltip: {
      title: 'Lucky Clover',
      stats: [{ stat: 'luck', value: 2 }],
      note: 'Granted to players who won a duel against all odds.',
    },
  },
  {
    url: `${ACHIEVEMENTS_ROOT}/heart.png`,
    label: 'Buff: heart',
    tooltip: {
      title: 'Big Heart',
      stats: [{ stat: 'health', value: 2 }],
      note: 'Granted to players who never fled from a fight.',
    },
  },
  {
    url: `${ACHIEVEMENTS_ROOT}/wrench.png`,
    label: 'Buff: wrench',
    tooltip: {
      title: 'Master Mechanic',
      stats: [{ stat: 'strength', value: 1 }, { stat: 'shield', value: 1 }],
      note: 'Granted to players who fully upgraded their workshop.',
    },
  },
  {
    url: `${ACHIEVEMENTS_ROOT}/hat.png`,
    label: 'Buff: hat',
    tooltip: {
      title: 'Happy New Year 2027',
      stats: [{ stat: 'health', value: 1 }, { stat: 'luck', value: 1 }, { stat: 'shield', value: 1 }],
      note: "Granted to every player who completed Santa's challenge \"Christmas Mass\".",
    },
  },
] as const;

// Portrait turn-animation sheet (6x6 head frames) for the left-panel portrait.
// Declared here so the loader and the studio catalog share one source; the
// loader adds autoTrim. A sheet override must keep the 6x6 grid.
export const PORTRAIT_TURN_SHEET_ASSET = {
  url: `${UI_ROOT}/portrait-emoji/turn.png`,
  cols: 6,
  rows: 6,
  label: 'Portrait turn sheet',
} as const;

// UI audio - loaded via loadAudio (not loadUiTextures, which is textures only).
// Declared here so the game and the studio catalog share one path (no drift).
export const GONG_SFX_URL = `${UI_ROOT}/sfx/japan-gong.wav`;

// Battle music: looping fight track + short stingers (same key/tempo) played
// over a ducked loop on strong skill attacks. See render/BattleMusic.
export const BATTLE_MUSIC_URL = `${UI_ROOT}/music/battle-loop.mp3`;
export const STINGER_BRASS_URL = `${UI_ROOT}/music/stinger-brass.mp3`;

export type UiAudioDescriptor = { url: string; label: string };

export const UI_AUDIO: readonly UiAudioDescriptor[] = [
  { url: GONG_SFX_URL, label: 'Battle gong' },
  { url: BATTLE_MUSIC_URL, label: 'Battle music loop' },
  { url: STINGER_BRASS_URL, label: 'Music stinger: brass' },
] as const;
